import { isOutdoorMap, mapLabel } from "@/lib/office/map-registry";
import { STATUSES } from "../constants";
import type { VyllageSceneState } from "../use-vyllage-state";
import { ChevronIcon, FlameIcon, StarIcon } from "./scene-icons";

/** Top bar: identity + presence + zone + points/streak + status. Secondary pills
 *  collapse away on narrow viewports so the bar stays usable on tablet/phone. */
export function SceneAppBar({ s, onLeave }: { s: VyllageSceneState; onLeave: () => void }) {
  const outdoor = isOutdoorMap(s.mapId);
  const mapLabelText = mapLabel(s.mapId);
  const peerPlace = outdoor ? "campus" : mapLabelText;
  const zoneSub = s.zonePersonal
    ? s.zoneDoorStatus === "open"
      ? "· open to guests"
      : "· closed · private"
    : s.zonePriv
      ? "· private audio"
      : s.zone === "Focus Zone"
        ? "· muffled"
        : outdoor
          ? "· outdoor"
          : "";

  return (
    <header className="flex h-[54px] shrink-0 items-center gap-2 bg-[var(--vy-bar)] px-3 sm:gap-3.5 sm:px-4">
      <div className="flex min-w-0 items-center gap-2.5">
        <img src="/assets/nymbl-logo-white.png" alt="Nymbl" className="block h-[15px]" />
        <span
          className="text-[13px] font-bold tracking-[0.22em] text-[var(--vy-accent)]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          VYLLAGE
        </span>
        <span className="hidden h-[18px] w-px bg-white/18 lg:block" />
        <span className="hidden text-xs font-medium text-white/60 lg:inline">
          Nymbl Village · Quincy, MA
        </span>
        <span
          className="ml-1 hidden rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide sm:inline"
          style={{
            color: outdoor ? "var(--vy-good-fg)" : "var(--vy-accent-fg)",
            background: outdoor ? "var(--vy-good-bg)" : "var(--vy-accent-bg)",
            borderColor: outdoor ? "var(--vy-good-border)" : "var(--vy-accent-border)",
          }}
          title="Current map"
        >
          {mapLabelText}
        </span>
        <span
          className="ml-0.5 rounded-full border border-white/12 px-2 py-0.5 text-[10px] font-semibold tracking-wide"
          style={{
            color:
              s.connection === "connected"
                ? "var(--vy-good)"
                : s.connection === "connecting"
                  ? "var(--vy-warn)"
                  : "var(--vy-fg-faint)",
            background: "var(--vy-glass)",
          }}
          title={s.connectionError ?? undefined}
        >
          {s.connection === "connected"
            ? `${s.peerCount + 1} in ${peerPlace}`
            : s.connection === "connecting"
              ? "connecting…"
              : s.entered
                ? "offline"
                : "local"}
        </span>
      </div>

      <div className="hidden flex-1 justify-center sm:flex">
        <div className="flex items-center gap-2 rounded-full border border-white/12 bg-white/8 px-3.5 py-1.5">
          <span className="h-[7px] w-[7px] rounded-full bg-[var(--vy-accent)]" />
          <span className="text-xs font-semibold text-white">{s.zone}</span>
          {zoneSub ? (
            <span className="hidden text-[11px] text-white/55 md:inline">{zoneSub}</span>
          ) : null}
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:ml-0">
        <button
          type="button"
          onClick={onLeave}
          className="cursor-pointer rounded-full border border-white/18 bg-white/10 px-3 py-1.5 text-xs font-bold tracking-wide text-white hover:bg-white/18"
          style={{ fontFamily: "var(--font-body)" }}
          title="Return to the ready room"
        >
          Leave <span className="hidden sm:inline">Vyllage</span>
        </button>
        <div
          title="Vyllage points"
          className="flex items-center gap-1.5 rounded-full border border-[var(--vy-accent-border)] bg-[var(--vy-accent-bg)] px-3 py-1.5"
        >
          <StarIcon />
          <span
            className="text-xs font-bold text-white"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {s.points}
          </span>
          <span className="hidden text-[10px] tracking-wide text-white/55 sm:inline">PTS</span>
        </div>
        <div
          title="Check-in streak"
          className="hidden items-center gap-1.5 rounded-full border border-white/12 bg-white/8 px-3 py-1.5 md:flex"
        >
          <FlameIcon />
          <span className="text-xs font-semibold text-white">{s.streak}-day</span>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => s.setStatusMenuOpen(!s.statusMenuOpen)}
            className="flex cursor-pointer items-center gap-1.5 rounded-full border border-white/14 bg-white/8 px-3 py-1.5 hover:bg-white/16"
            style={{ fontFamily: "var(--font-body)" }}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: s.statusMeta.color }} />
            <span className="hidden text-xs font-semibold text-white sm:inline">
              {s.statusMeta.label}
            </span>
            <ChevronIcon />
          </button>
          {s.statusMenuOpen ? (
            <div className="absolute top-9 right-0 z-[60] flex w-[168px] flex-col gap-0.5 rounded-xl border border-[var(--vy-surface-border)] bg-[var(--vy-surface)] p-1.5 shadow-[var(--shadow-md)]">
              {STATUSES.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => s.setStatusManual(o.id)}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border-0 px-2.5 py-2 text-left hover:bg-[var(--vy-surface-hover)]"
                  style={{
                    fontFamily: "var(--font-body)",
                    background: o.id === s.status ? "var(--vy-surface-accent)" : "transparent",
                  }}
                >
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: o.color }} />
                  <span className="text-[12.5px] font-semibold text-[var(--vy-surface-fg)]">
                    {o.label}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
